import type { Edges, NodeId, NodeMap } from './types';
import { isCollapsedAway } from './graph';
import { isPermHidden, type HiddenSets } from './permissions';

/**
 * "Is this node on screen?" — the composition of the two independent reasons it might not be.
 *
 * The vanilla `isHidden()` folds both into one function:
 *
 *     function isHidden(id){ if(permHidden(id))return true; ...collapsed ancestor walk... }
 *
 * which is convenient and untestable: it reads `nodes`, `edges`, `forceOpen`, `perm` and
 * `hiddenNodeSet` from module scope, so exercising the collapse logic means standing up a
 * permissions payload too. Split here into `isCollapsedAway` (lib/graph) and `isPermHidden`
 * (lib/permissions), each testable alone, and recombined by this function for the callers
 * that genuinely want "hidden, for any reason".
 *
 * The order matters and is preserved: permissions first. A node the group cannot see is
 * hidden whatever its ancestors are doing, and asking the cheaper question first also avoids
 * walking the tree for a node that was never going to be shown.
 */
export interface VisibilityContext {
  nodes: NodeMap;
  edges: Edges;
  hidden: HiddenSets;
  /** Ids temporarily treated as expanded to reveal a search or tag hit. Transient. */
  forceOpen?: ReadonlySet<NodeId>;
}

export function isHidden(id: NodeId, ctx: VisibilityContext): boolean {
  if (isPermHidden(id, ctx.edges, ctx.hidden)) return true;
  return isCollapsedAway(id, {
    edges: ctx.edges,
    collapsed: (nid) => !!ctx.nodes[nid]?.collapsed,
    forceOpen: ctx.forceOpen,
  });
}

/**
 * Every id that renders, in the map's own key order.
 *
 * The vanilla renderer iterates `nodes` and skips hidden ones inline; having the set as a
 * value is what lets `NodeLayer` memoise on it and lets a test assert the visible set
 * directly instead of counting DOM nodes.
 */
export function visibleNodeIds(ctx: VisibilityContext): NodeId[] {
  const out: NodeId[] = [];
  for (const id in ctx.nodes) if (!isHidden(id, ctx)) out.push(id);
  return out;
}
